import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSelector, useDispatch } from "react-redux";
import { initializeUserCart, removeFromCart } from "../../store/slice/cart";
import isValidURL from "../../ValidateUrl";
import convertTo12Hour from "../../convertTo12Hours";
import { useMemo } from "react";

export const CartPage = () => {
  const dispatch = useDispatch();
  const { cartItem, currentUserId } = useSelector((state) => state.cart);

  // Pull user and auth data directly from localStorage
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);

  const formatDeliveryDate = (dateString) => {
    if (!dateString) return "Date unavailable";

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);

    if (!match) return "Date unavailable";

    const [, year, month, day] = match;

    const date = new Date(Number(year), Number(month) - 1, Number(day));

    return date.toDateString();
  };

  const authToken = localStorage.getItem("auth");

  const isAuthenticated = Boolean(authToken && storedUser?.id);

  // Handler to remove an item – dispatches the synchronous removeFromCart action
  const handleRemoveItem = (chefIndex, menuIndex) => {
    dispatch(removeFromCart({ chefIndex, menuIndex }));
  };

  // Show login prompt if user is not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Header />
        <main className="container mx-auto py-12">
          <section className="text-center lg:text-start">
            <h1 className="font-semibold text-3xl uppercase text-secondary tracking-widest">
              Your cart
            </h1>
            <div className="w-[60px] h-[2px] bg-primary my-6 mx-auto lg:mx-0"></div>
          </section>
          <section className="flex justify-center items-center min-h-[300px]">
            <div className="w-full max-w-md bg-[#f7f7f7] p-8 rounded-lg shadow-md text-center">
              {/* Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="72"
                height="72"
                fill="#dcdcdc"
                className="mx-auto mb-4"
              >
                <path d="M4 4V2C4 1.44772 4.44772 1 5 1H19C19.5523 1 20 1.44772 20 2V4H22V6H20V20C20 20.5523 19.5523 21 19 21H5C4.44772 21 4 20.5523 4 20V6H2V4H4ZM6 6V19H18V6H6ZM6 3V4H18V3H6Z"></path>
              </svg>

              {/* Message */}
              <h2 className="text-lg font-medium text-secondary mb-4">
                Please log in to view your cart
              </h2>
              <Link
                to="/login"
                className="inline-block bg-primary px-6 rounded-lg hover:bg-primary/90 transition-all"
              >
                <p className="text-white text-sm font-semibold mt-2">Sign in</p>
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container mx-auto py-12">
        <div className="lg:text-start text-center">
          <h1 className="font-semibold text-3xl uppercase text-secondary tracking-widest">
            Your cart
          </h1>
          <div className="w-[60px] h-[2px] bg-primary my-6 lg:mx-0 mx-auto"></div>

          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === "development" && (
            <p className="text-xs text-gray-500 mb-4">
              User ID: {currentUserId} | Items: {cartItem?.length || 0}
            </p>
          )}
        </div>

        <div className="grid grid-cols-12 gap-4">
          {cartItem &&
            cartItem.length > 0 &&
            cartItem.map((chef, chefIndex) => (
              <div
                key={`${chef.id}-${chefIndex}`}
                className="lg:col-span-4 md:col-span-6 col-span-12"
              >
                <div className="p-3 rounded-lg border border-primaryLight bxShadow">
                  <div className="flex justify-between gap-3">
                    <div className="flex items-center gap-x-4">
                      <img
                        src={
                          chef.profile_pic && isValidURL(chef.profile_pic)
                            ? chef.profile_pic
                            : "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                        }
                        className="object-top rounded-lg w-[80px] object-cover h-[80px]"
                        alt="chef profile"
                      />
                      <div className="block">
                        <h2 className="!text-secondary text-2xl font-semibold leading-tight mb-1">
                          {`${chef.first_name || ""} ${chef.last_name || ""}`}
                        </h2>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between gap-3 mt-6 px-3">
                    <h2 className="mb-1 text-lg font-semibold leading-tight">
                      Items
                    </h2>
                    <h2 className="mb-1 text-lg font-semibold leading-tight">
                      Qty
                    </h2>
                  </div>

                  {chef.menu.map((menu, menuIndex) => (
                    <div
                      key={`${menu.id}-${menuIndex}`}
                      className="flex items-center justify-between border border-primary border-dashed rounded-lg p-2 gap-x-2 mt-4"
                    >
                      <div className="flex items-center gap-x-2 w-[80%]">
                        <img
                          src={
                            menu.logo && isValidURL(menu.logo)
                              ? menu.logo
                              : "https://t4.ftcdn.net/jpg/04/70/29/97/360_F_470299797_UD0eoVMMSUbHCcNJCdv2t8B2g1GVqYgs.jpg"
                          }
                          className="object-top rounded-lg w-[70px] object-cover h-[60px]"
                          alt="menu item"
                        />
                        <h3 className="mb-1 text-lg font-semibold leading-tight">
                          {menu.name}
                          <div className="flex flex-col">
                            <p className="text-xs my-0 text-headGray mt-[2px] tracking-wider">
                              Date: {formatDeliveryDate(chef.delivery_date)}
                              {console.log(
                                "DELIVERY DATE:",
                                chef.delivery_date,
                              )}
                              {console.log("TYPE:", typeof chef.delivery_date)}
                              {console.log(
                                "PARSED:",
                                new Date(chef.delivery_date),
                              )}
                            </p>
                            <p className="text-[10px] mt-1 xl:text-xs my-0 text-headGray tracking-wider">
                              Slot:{" "}
                              {convertTo12Hour(
                                chef.delivery_slot?.split("-")[0],
                              ) +
                                " - " +
                                convertTo12Hour(
                                  chef.delivery_slot?.split("-")[1],
                                )}
                            </p>
                          </div>
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <h6 className="text-lg font-semibold leading-tight text-right mb-0">
                          {menu.quantity}x
                        </h6>
                        <button
                          onClick={() => handleRemoveItem(chefIndex, menuIndex)}
                          className="text-red-600 hover:text-red-800 transition-colors p-1"
                          aria-label="Remove item"
                          title="Remove item"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            fill="currentColor"
                          >
                            <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="mt-8 text-center">
                    <Link
                      to={`/checkout/${chef.id}/${chefIndex}`}
                      className="flex justify-center items-center gap-x-3 bg-primary rounded-lg py-3 px-3 hover:bg-primary/90 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="rgba(255,255,255,1)"
                      >
                        <path d="M4.00488 16V4H2.00488V2H5.00488C5.55717 2 6.00488 2.44772 6.00488 3V15H18.4433L20.4433 7H8.00488V5H21.7241C22.2764 5 22.7241 5.44772 22.7241 6C22.7241 6.08176 22.7141 6.16322 22.6942 6.24254L20.1942 16.2425C20.083 16.6877 19.683 17 19.2241 17H5.00488C4.4526 17 4.00488 16.5523 4.00488 16ZM6.00488 23C4.90031 23 4.00488 22.1046 4.00488 21C4.00488 19.8954 4.90031 19 6.00488 19C7.10945 19 8.00488 19.8954 8.00488 21C8.00488 22.1046 7.10945 23 6.00488 23ZM18.0049 23C16.9003 23 16.0049 22.1046 16.0049 21C16.0049 19.8954 16.9003 19 18.0049 19C19.1095 19 20.0049 19.8954 20.0049 21C20.0049 22.1046 19.1095 23 18.0049 23Z"></path>
                      </svg>
                      <span className="text-white text-xl font-semibold">
                        To Checkout
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}

          {(!cartItem || cartItem.length < 1) && (
            <div className="lg:col-span-4 col-span-12 bg-[#f7f7f7]">
              <div className="pt-12 pb-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto mb-3"
                  viewBox="0 0 24 24"
                  width="96"
                  height="96"
                  fill="#dcdcdc"
                >
                  <path d="M6.50488 2H17.5049C17.8196 2 18.116 2.14819 18.3049 2.4L21.0049 6V21C21.0049 21.5523 20.5572 22 20.0049 22H4.00488C3.4526 22 3.00488 21.5523 3.00488 21V6L5.70488 2.4C5.89374 2.14819 6.19013 2 6.50488 2ZM19.0049 8H5.00488V20H19.0049V8ZM18.5049 6L17.0049 4H7.00488L5.50488 6H18.5049ZM9.00488 10V12C9.00488 13.6569 10.348 15 12.0049 15C13.6617 15 15.0049 13.6569 15.0049 12V10H17.0049V12C17.0049 14.7614 14.7663 17 12.0049 17C9.24346 17 7.00488 14.7614 7.00488 12V10H9.00488Z"></path>
                </svg>
                <h2 className="text-lg text-center text-secondary">
                  Your cart is empty <br /> Add items to get started
                </h2>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CartPage;
